((host, factory) => {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (!host?.document) return;
  if (host.TimeseekMarketHistoryInteraction) {
    host.TimeseekMarketHistoryInteraction.boot(host.document);
    return;
  }
  host.TimeseekMarketHistoryInteraction = api;
  const start = () => api.boot(host.document);
  if (host.document.readyState === 'loading') host.document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  host.document.addEventListener('shopify:section:load', (event) => api.boot(event.target));
})(typeof window !== 'undefined' ? window : globalThis, () => {
  'use strict';
  const statuses = new Set(['available', 'stale', 'missing', 'ambiguous']);
  const DAY = 86400000;
  const NS = 'http://www.w3.org/2000/svg';
  const LEFT = 128, RIGHT = 632, TOP = 38, BOTTOM = 308;
  let viewId = 0;
  const formatter = new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 2 });
  const priceFormatter = new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 });
  const countFormatter = new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 1 });
  const numeric = (value, integer = false) => {
    if (value === '') return null;
    if (!/^(?:\d+(?:\.\d+)?)(?:e[+-]?\d+)?$/.test(value)) throw Error('invalid_history_number');
    const number = Number(value);
    if (!Number.isFinite(number) || number < 0 || (integer && !Number.isSafeInteger(number))) throw Error('invalid_history_number');
    return number;
  };
  function parseRows(raw) {
    if (typeof raw !== 'string' || raw.length > 30000 || !raw) throw Error('invalid_history_rows');
    let previous = '';
    const rows = raw.split('|').map((row) => {
      const f = row.split(':');
      if (f.length !== 8 || !/^\d{4}-\d{2}-\d{2}$/.test(f[0]) || f[0] <= previous || !statuses.has(f[4])) throw Error('invalid_history_rows');
      const time = Date.parse(f[0] + 'T00:00:00Z');
      if (!Number.isFinite(time) || new Date(time).toISOString().slice(0, 10) !== f[0]) throw Error('invalid_history_date');
      previous = f[0];
      const result = { date: f[0], minimum: numeric(f[1]), maximum: numeric(f[2]), count: numeric(f[3], true), status: f[4], observed: numeric(f[5], true), stock: numeric(f[6], true), median: numeric(f[7]) };
      if (result.minimum !== null && result.maximum !== null && result.minimum > result.maximum) throw Error('invalid_history_range');
      if (result.median !== null && ((result.minimum !== null && result.minimum > result.median) || (result.maximum !== null && result.median > result.maximum))) throw Error('invalid_history_range');
      if (result.stock !== null && result.count !== null && result.stock > result.count) throw Error('invalid_history_stock');
      if (['missing', 'ambiguous'].includes(result.status) && [result.minimum, result.maximum, result.count, result.median].some((value) => value !== null)) throw Error('invalid_history_missing');
      return result;
    });
    if (rows.length > 90 || (Date.parse(rows.at(-1).date) - Date.parse(rows[0].date)) / 86400000 > 89) throw Error('invalid_history_window');
    return rows;
  }
  function displayValue(row, key, labels) {
    if (key === 'status') return labels[row.status];
    const value = row[key];
    if (value === null) return row.status === 'ambiguous' && ['median', 'count'].includes(key) ? labels.ambiguous : labels.missing;
    return (key === 'count' ? countFormatter : priceFormatter).format(value) + (key === 'count' ? labels.countUnit : labels.priceUnit);
  }
  function filterRows(rows, range) {
    if (![30, 90].includes(Number(range)) || !rows.length) throw Error('invalid_history_range');
    const end = Date.parse(rows.at(-1).date + 'T00:00:00Z');
    return rows.filter((row) => Date.parse(row.date + 'T00:00:00Z') >= end - (Number(range) - 1) * DAY);
  }
  function aggregateMonths(rows) {
    const months = new Map();
    for (const row of rows) {
      const month = row.date.slice(0, 7);
      if (!months.has(month)) months.set(month, []);
      months.get(month).push(row);
    }
    const average = (values) => {
      if (!values.length) return null;
      const maximum = Math.max(...values);
      if (maximum === 0) return 0;
      if (values.every((value) => value === maximum)) return maximum;
      // Normalization prevents overflow when several values approach the
      // largest finite number; rounding cannot push a mean above its maximum.
      return Math.min(1, values.reduce((sum, value) => sum + value / maximum / values.length, 0)) * maximum;
    };
    return [...months].map(([date, days]) => {
      const valued = days.filter((row) => row.median !== null);
      const low = days.map((row) => row.minimum).filter((value) => value !== null);
      const high = days.map((row) => row.maximum).filter((value) => value !== null);
      return {
        date, median: average(valued.map((row) => row.median)),
        minimum: low.length ? Math.min(...low) : null,
        maximum: high.length ? Math.max(...high) : null,
        count: average(days.map((row) => row.count).filter((value) => value !== null)),
        status: valued.length ? (valued.every((row) => row.status === 'stale') ? 'stale' : 'available') : days.some((row) => row.status === 'ambiguous') ? 'ambiguous' : 'missing',
      };
    });
  }
  function createScale(values, zeroBase = false, focused = false) {
    let minimum = zeroBase ? 0 : Math.min(...values), maximum = Math.max(...values);
    const niceStep = (value) => {
      if (!Number.isFinite(value) || value <= 0) return 1;
      const magnitude = Math.max(Number.MIN_VALUE, 10 ** Math.floor(Math.log10(value)));
      const fraction = value / magnitude;
      return (fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10) * magnitude;
    };
    if (!zeroBase) {
      const midpoint = minimum + (maximum - minimum) / 2;
      const halfSpan = focused ? .025 : .05;
      if (midpoint > 0 && maximum - minimum < midpoint * halfSpan * 2) {
        minimum = midpoint * (1 - halfSpan);
        maximum = Math.min(Number.MAX_VALUE, midpoint * (1 + halfSpan));
      }
    }
    if (maximum === minimum) {
      minimum = minimum > 0 ? minimum * .9 : 0;
      maximum = maximum > 0 ? Math.min(Number.MAX_VALUE, maximum * 1.1) : 1;
    }
    if (!zeroBase && minimum > 0 && maximum / minimum >= 20) {
      const boundary = (value, floor) => {
        const magnitude = Math.max(Number.MIN_VALUE, 10 ** Math.floor(Math.log10(value)));
        const steps = [1, 2, 5, 10], fraction = value / magnitude;
        const step = (floor ? [...steps].reverse().find((item) => item <= fraction) : steps.find((item) => item >= fraction)) ?? (floor ? 1 : 10);
        return Math.max(Number.MIN_VALUE, Math.min(Number.MAX_VALUE, step * magnitude));
      };
      minimum = boundary(minimum, true); maximum = boundary(maximum, false);
      const candidates = [];
      for (let exponent = Math.floor(Math.log10(minimum)); exponent <= Math.ceil(Math.log10(maximum)); exponent++) for (const factor of [1, 2, 5]) {
        const value = factor * 10 ** exponent;
        if (Number.isFinite(value) && value >= minimum && value <= maximum && value > 0) candidates.push(value);
      }
      if (!candidates.includes(minimum)) candidates.unshift(minimum);
      if (!candidates.includes(maximum)) candidates.push(maximum);
      const ticks = candidates.length <= 5 ? candidates : [...new Set(Array.from({ length: 5 }, (_, index) => candidates[Math.round(index * (candidates.length - 1) / 4)]))];
      return { type: 'log', ticks, y: (value) => BOTTOM - (Math.log10(Math.max(value, minimum)) - Math.log10(minimum)) / (Math.log10(maximum) - Math.log10(minimum)) * (BOTTOM - TOP) };
    }
    // Work in a normalized domain so even the largest finite prices remain
    // finite during tick rounding. Prices and counts themselves never change.
    const unit = maximum || 1, lower = minimum / unit;
    let low = lower, high = 1;
    if (zeroBase) high = niceStep(1 / 4) * 4;
    else {
      const step = niceStep((1 - lower) / 4);
      low = Math.max(0, Math.floor(lower / step) * step);
      high = Math.ceil(1 / step) * step;
      if (high <= low) high = low + step;
    }
    // Counts need integer ticks on a zero baseline, including 0/1/2/3/4 for
    // small populations. The guard keeps Number.MAX_SAFE_INTEGER bounded.
    if (zeroBase && maximum <= Number.MAX_SAFE_INTEGER) {
      const top = Math.min(Number.MAX_SAFE_INTEGER, Math.max(1, niceStep(maximum / 4)) * 4);
      return { type: 'linear', ticks: Array.from({ length: 5 }, (_, index) => top * index / 4), y: (value) => BOTTOM - value / top * (BOTTOM - TOP) };
    }
    return { type: 'linear', ticks: Array.from({ length: 5 }, (_, index) => Math.min(Number.MAX_VALUE, (low + index * (high - low) / 4) * unit)), y: (value) => BOTTOM - (value / unit - low) / (high - low) * (BOTTOM - TOP) };
  }
  function buildGeometry(rows, granularity = 'daily', priceView = 'range') {
    if (!['daily', 'monthly'].includes(granularity) || !['focus', 'range'].includes(priceView) || !rows.length) throw Error('invalid_history_geometry');
    const epoch = (row) => granularity === 'monthly' ? Number(row.date.slice(0, 4)) * 12 + Number(row.date.slice(5, 7)) : Date.parse(row.date + 'T00:00:00Z') / DAY;
    const prices = rows.filter((row) => row.median !== null), counts = rows.filter((row) => row.count !== null);
    const priceValues = priceView === 'focus' ? prices.map((row) => row.median) : prices.flatMap((row) => [row.minimum, row.median, row.maximum]).filter((value) => value !== null);
    const priceScale = prices.length ? createScale(priceValues, false, priceView === 'focus') : null;
    const countScale = counts.length ? createScale(counts.map((row) => row.count), true) : null;
    const first = epoch(rows[0]), last = epoch(rows.at(-1));
    const x = (row) => last === first ? (LEFT + RIGHT) / 2 : LEFT + (epoch(row) - first) / (last - first) * (RIGHT - LEFT);
    const rounded = (value) => { if (!Number.isFinite(value)) throw Error('invalid_history_geometry'); return String(Number(value.toFixed(2))); };
    const nodes = [], positioned = [];
    const add = (tag, attrs, text) => nodes.push({ tag, attrs, ...(text === undefined ? {} : { text }) });
    const tick = (value) => {
      const label = formatter.format(value);
      return value !== 0 && (value < .01 || label.length > 8) ? value.toExponential(1) : label;
    };
    const leftScale = priceScale ?? countScale;
    if (leftScale) for (const value of leftScale.ticks) {
      const y = rounded(leftScale.y(value));
      add('line', { class: 'ts-market-history-grid', x1: LEFT, y1: y, x2: RIGHT, y2: y });
      add('text', { class: `ts-market-history-tick ts-market-history-tick--${priceScale ? 'price' : 'count'}`, x: LEFT - 12, y, 'text-anchor': 'end', 'dominant-baseline': 'middle' }, tick(priceScale ? value / 10000 : value));
    }
    if (priceScale && countScale) for (const value of countScale.ticks) add('text', { class: 'ts-market-history-tick ts-market-history-tick--count', x: RIGHT + 12, y: rounded(countScale.y(value)), 'text-anchor': 'start', 'dominant-baseline': 'middle' }, tick(value));
    add('path', { class: 'ts-market-history-axis', d: `M${LEFT} ${TOP}V${BOTTOM}H${RIGHT}${priceScale ? `V${TOP}` : ''}` });
    const tickRows = [...new Set([rows[0], rows[Math.floor((rows.length - 1) / 2)], rows.at(-1)])];
    for (const row of tickRows) add('text', { class: 'ts-market-history-tick ts-market-history-tick--date', x: rounded(x(row)), y: 340, 'text-anchor': row === rows[0] ? 'start' : row === rows.at(-1) ? 'end' : 'middle' }, row.date);
    const runs = (predicate) => {
      const groups = []; let current = [];
      for (const row of rows) {
        if (!predicate(row)) { if (current.length) groups.push(current); current = []; continue; }
        if (current.length && epoch(row) - epoch(current.at(-1)) !== 1) { groups.push(current); current = []; }
        current.push(row);
      }
      if (current.length) groups.push(current);
      return groups;
    };
    if (priceScale && priceView === 'range') for (const run of runs((row) => row.median !== null && row.minimum !== null && row.maximum !== null)) {
      if (run.length === 1) {
        const row = run[0], upper = priceScale.y(row.maximum), lower = priceScale.y(row.minimum);
        add('rect', { class: 'ts-market-history-price-range', x: rounded(x(row) - 8), y: rounded(upper), width: 16, height: rounded(Math.max(2, lower - upper)) });
      } else {
        const upper = run.map((row, index) => `${index ? 'L' : 'M'}${rounded(x(row))} ${rounded(priceScale.y(row.maximum))}`).join('');
        const lower = [...run].reverse().map((row) => `L${rounded(x(row))} ${rounded(priceScale.y(row.minimum))}`).join('');
        add('path', { class: 'ts-market-history-price-range', d: `${upper}${lower}Z` });
      }
    }
    if (countScale) for (const row of counts) {
      const y = countScale.y(row.count), width = Math.max(2, Math.min(12, (RIGHT - LEFT) / Math.max(counts.length, 1) * .48));
      add('rect', { class: 'ts-market-history-count-bar', 'data-observed-date': row.date, 'data-value': row.count, x: rounded(x(row) - width / 2), y: rounded(y), width: rounded(width), height: rounded(BOTTOM - y) });
    }
    if (priceScale) for (const run of runs((row) => row.median !== null)) {
      if (run.length > 1) add('path', { class: 'ts-market-history-price-line', d: run.map((row, index) => `${index ? 'L' : 'M'}${rounded(x(row))} ${rounded(priceScale.y(row.median))}`).join('') });
      for (const row of run) {
        const point = { tag: 'circle', attrs: { class: 'ts-market-history-point ts-market-history-price-line', 'data-observed-date': row.date, 'data-value': row.median, 'data-price-min': row.minimum ?? '', 'data-price-max': row.maximum ?? '', cx: rounded(x(row)), cy: rounded(priceScale.y(row.median)), r: run.length === 1 ? 4.5 : 0 } };
        nodes.push(point); positioned.push({ date: row.date, x: x(row), specification: point });
      }
    }
    return { nodes, positioned, priceScaleType: priceScale?.type ?? 'none' };
  }
  function attach(root) {
    if (root.dataset.historyExternal === 'pending') return;
    if (['ready', 'fallback'].includes(root.dataset.historyInteraction) || root.dataset.historyPrice !== 'true') return;
    const select = root.querySelector('[data-history-day]');
    const controls = root.querySelector('[data-history-controls]');
    const date = root.querySelector('[data-history-selected-date]');
    const svg = root.querySelector('.ts-market-history-chart');
    const cells = [...root.querySelectorAll('[data-history-value]')];
    if (!select || !controls || !date || !svg || cells.length !== 5) return;
    let rows;
    try { rows = parseRows(root.dataset.historyRows); } catch { return; }
    const options = [...select.options];
    if (options.length !== rows.length || options.some((option, index) => option.value !== rows[index].date)) return;
    const labels = { priceUnit: root.dataset.historyPriceUnit, countUnit: root.dataset.historyCountUnit, missing: root.dataset.historyMissingLabel, ambiguous: root.dataset.historyAmbiguousLabel, available: root.dataset.historyAvailableLabel, stale: root.dataset.historyStaleLabel };
    if (Object.values(labels).some((value) => !value)) return;
    let displayed = rows, byDate = new Map(rows.map((row) => [row.date, row]));
    let positioned = [...svg.querySelectorAll('circle[data-observed-date]')].map((point) => ({ date: point.getAttribute('data-observed-date'), x: Number(point.getAttribute('cx')), point }));
    if (positioned.some((point) => !Number.isFinite(point.x) || !byDate.has(point.date))) return;
    let active = null;
    const selectDay = (day) => {
      const row = byDate.get(day);
      if (!row) return;
      select.value = day;
      date.textContent = day;
      date.setAttribute('datetime', day);
      for (const cell of cells) cell.textContent = displayValue(row, cell.dataset.historyValue, labels);
      for (const point of positioned) point.point.classList.toggle('is-selected', point.date === day);
      active = day;
    };
    select.addEventListener('change', () => selectDay(select.value));
    const rangeButtons = [...root.querySelectorAll('[data-history-range]')];
    const granularityButtons = [...root.querySelectorAll('[data-history-granularity]')];
    const priceViewButtons = [...root.querySelectorAll('[data-history-price-view]')];
    const focusNote = root.querySelector('[data-history-focus-note]');
    const rangeNote = root.querySelector('[data-history-range-note]');
    const rangeLegend = root.querySelector('[data-history-range-legend]');
    const originalRangeLegendHidden = rangeLegend?.hidden;
    const rangeBandNote = root.querySelector('[data-history-range-band-note]');
    const originalRangeBandNoteHidden = rangeBandNote?.hidden;
    const note = root.querySelector('[data-history-granularity-note]');
    const displayControls = root.querySelector('[data-history-display-controls]');
    const logNote = root.querySelector('[data-history-log-note]');
    const originalLogHidden = logNote?.hidden;
    const period = root.querySelector('.ts-market-history__period');
    const periodDates = period ? [...period.querySelectorAll('time')] : [];
    const originalPeriod = periodDates.map((node) => ({ text: node.textContent, datetime: node.getAttribute('datetime') }));
    const contextLabels = [
      ['select-label', 'SelectedDay', 'SelectedMonth'],
      ['selection-label', 'SelectedDay', 'SelectedMonth'],
      ['price-legend-label', 'DailyPrice', 'MonthlyPrice'],
      ['count-legend-label', 'DailyCount', 'MonthlyCount'],
      ['price-value-label', 'DailyPrice', 'MonthlyPrice'],
      ['count-value-label', 'DailyCount', 'MonthlyCount'],
    ].map(([marker, daily, monthly]) => ({ element: root.querySelector(`[data-history-${marker}]`), daily: `history${daily}Label`, monthly: `history${monthly}Label` })).filter(({ element }) => element).map((entry) => ({ ...entry, original: entry.element.textContent }));
    const updateContextLabels = (monthly) => {
      for (const { element, daily, monthly: monthKey, original } of contextLabels) {
        const label = root.dataset[monthly ? monthKey : daily];
        // Older snippets have neither these nodes nor the corresponding
        // datasets; their existing labels remain untouched.
        element.textContent = typeof label === 'string' && label ? label : original;
      }
    };
    const originalChildren = [...svg.childNodes], originalScale = svg.getAttribute('data-price-scale');
    const descriptions = originalChildren.filter((node) => ['title', 'desc'].includes(node.localName));
    const originalAria = ['aria-labelledby', 'aria-describedby'].map((name) => [name, svg.getAttribute(name)]);
    const viewBase = descriptions.find((node) => node.localName === 'title')?.getAttribute('id') || `TsMarketHistoryView-${++viewId}`;
    const heading = root.querySelector('.ts-market-history__chart-panel .ts-market-history__heading') || root.querySelector('.ts-market-history__heading');
    const currentLabel = (marker, monthly) => {
      const entry = contextLabels.find(({ element }) => element === root.querySelector(`[data-history-${marker}]`));
      if (!entry) return '';
      const value = root.dataset[monthly ? entry.monthly : entry.daily];
      return typeof value === 'string' && value ? value : entry.original;
    };
    const staticValueLabels = new Map(cells.map((cell) => [cell.dataset.historyValue, cell.previousElementSibling?.textContent?.trim() || '']));
    const accessibilityText = (window, nextRows, priceScaleType) => {
      const monthly = granularity === 'monthly';
      const priceLabel = currentLabel('price-value-label', monthly) || currentLabel('price-legend-label', monthly) || staticValueLabels.get('median');
      const countLabel = currentLabel('count-value-label', monthly) || currentLabel('count-legend-label', monthly) || staticValueLabels.get('count');
      const selectedRange = rangeButtons.find((button) => Number(button.dataset.historyRange) === range)?.textContent?.trim();
      const selectedGranularity = granularityButtons.find((button) => button.dataset.historyGranularity === granularity)?.textContent?.trim();
      const selectedPriceView = priceViewButtons.find((button) => button.dataset.historyPriceView === priceView)?.textContent?.trim();
      const title = [heading?.textContent?.trim(), selectedRange, selectedGranularity, selectedPriceView, `${window[0].date} / ${window.at(-1).date}`].filter(Boolean).join(' / ');
      const legend = root.querySelector('.ts-market-history__legend');
      let legendText = legend?.textContent?.trim() || '';
      for (const marker of ['price-legend-label', 'count-legend-label']) {
        const node = root.querySelector(`[data-history-${marker}]`), nextLabel = currentLabel(marker, monthly);
        if (node?.textContent && nextLabel) legendText = legendText.replace(node.textContent, nextLabel);
      }
      const observed = nextRows.map((row) => [row.date, ...['median', 'minimum', 'maximum', 'count', 'status'].map((key) => {
        const label = key === 'median' ? priceLabel : key === 'count' ? countLabel : staticValueLabels.get(key);
        return [label, displayValue(row, key, labels)].filter(Boolean).join(': ');
      })].join('; ')).join(' | ');
      // All prose comes from reviewed server-rendered labels and method text.
      // The original full daily description stays in DOM, but screen readers
      // reference this description of the actual filtered/monthly view.
      if (priceView === 'focus' && rangeLegend?.textContent) legendText = legendText.replace(rangeLegend.textContent, '');
      const viewNote = priceView === 'focus' ? focusNote : rangeNote;
      return { title, description: [title, legendText, viewNote?.textContent?.trim(), monthly ? note?.textContent?.trim() : '', priceScaleType === 'log' ? logNote?.textContent?.trim() : '', observed].filter(Boolean).join(' | ') };
    };
    let range = 30, granularity = 'daily', priceView = priceViewButtons.length ? 'focus' : 'range';
    const doc = root.ownerDocument;
    const fallback = () => {
      svg.replaceChildren(...originalChildren);
      svg.setAttribute('data-price-scale', originalScale ?? 'none');
      for (const [name, value] of originalAria) { if (value === null) svg.removeAttribute(name); else svg.setAttribute(name, value); }
      select.replaceChildren(...options);
      displayed = rows; byDate = new Map(rows.map((row) => [row.date, row]));
      positioned = [...svg.querySelectorAll('circle[data-observed-date]')].map((point) => ({ date: point.getAttribute('data-observed-date'), x: Number(point.getAttribute('cx')), point }));
      if (note) note.hidden = true;
      if (logNote) logNote.hidden = originalLogHidden;
      if (focusNote) focusNote.hidden = true;
      if (rangeNote) rangeNote.hidden = true;
      if (rangeLegend) rangeLegend.hidden = originalRangeLegendHidden;
      if (rangeBandNote) rangeBandNote.hidden = originalRangeBandNoteHidden;
      if (displayControls) displayControls.hidden = true;
      root.dataset.historyDisplayGranularity = 'daily';
      root.dataset.historyPriceView = 'range';
      for (const { element, original } of contextLabels) element.textContent = original;
      periodDates.forEach((node, index) => { node.textContent = originalPeriod[index].text; node.setAttribute('datetime', originalPeriod[index].datetime); });
      controls.hidden = true;
      for (const button of [...rangeButtons, ...granularityButtons, ...priceViewButtons]) button.disabled = true;
      selectDay(rows.at(-1).date);
      root.dataset.historyInteraction = 'fallback';
    };
    const render = () => {
      try {
        const window = filterRows(rows, range);
        const nextRows = granularity === 'monthly' ? aggregateMonths(window) : window;
        const geometry = buildGeometry(nextRows, granularity, priceView);
        const group = doc.createElementNS(NS, 'g');
        group.setAttribute('data-history-enhanced', granularity);
        const actual = new Map();
        for (const specification of geometry.nodes) {
          const element = doc.createElementNS(NS, specification.tag);
          for (const [name, value] of Object.entries(specification.attrs)) element.setAttribute(name, value);
          if (specification.text !== undefined) element.textContent = specification.text;
          group.appendChild(element); actual.set(specification, element);
        }
        const nextOptions = nextRows.map((row) => {
          const option = doc.createElement('option'); option.value = row.date; option.textContent = row.date; return option;
        });
        const accessible = accessibilityText(window, nextRows, geometry.priceScaleType);
        const currentTitle = doc.createElementNS(NS, 'title');
        currentTitle.setAttribute('id', `${viewBase}-current-title`); currentTitle.textContent = accessible.title;
        const currentDescription = doc.createElementNS(NS, 'desc');
        currentDescription.setAttribute('id', `${viewBase}-current-description`); currentDescription.textContent = accessible.description;
        // Keep the original, full-window SVG title/description and SSR source
        // observations. Only the optional visual geometry is replaced.
        svg.replaceChildren(...descriptions, currentTitle, currentDescription, group);
        svg.setAttribute('aria-labelledby', currentTitle.getAttribute('id'));
        svg.setAttribute('aria-describedby', currentDescription.getAttribute('id'));
        svg.setAttribute('data-price-scale', geometry.priceScaleType);
        select.replaceChildren(...nextOptions);
        displayed = nextRows; byDate = new Map(nextRows.map((row) => [row.date, row]));
        positioned = geometry.positioned.map((point) => ({ ...point, point: actual.get(point.specification) }));
        for (const button of rangeButtons) button.setAttribute('aria-pressed', String(Number(button.dataset.historyRange) === range));
        for (const button of granularityButtons) button.setAttribute('aria-pressed', String(button.dataset.historyGranularity === granularity));
        for (const button of priceViewButtons) button.setAttribute('aria-pressed', String(button.dataset.historyPriceView === priceView));
        if (focusNote) focusNote.hidden = priceView !== 'focus';
        if (rangeNote) rangeNote.hidden = priceView !== 'range';
        if (rangeLegend) rangeLegend.hidden = priceView !== 'range';
        if (rangeBandNote) rangeBandNote.hidden = priceView !== 'range';
        if (note) note.hidden = granularity !== 'monthly';
        updateContextLabels(granularity === 'monthly');
        if (logNote) logNote.hidden = geometry.priceScaleType !== 'log';
        if (periodDates.length === 2) {
          for (const [index, row] of [window[0], window.at(-1)].entries()) { periodDates[index].textContent = row.date; periodDates[index].setAttribute('datetime', row.date); }
        }
        root.dataset.historyDisplayGranularity = granularity;
        root.dataset.historyPriceView = priceView;
        selectDay(nextRows.at(-1).date);
        return true;
      } catch { fallback(); return false; }
    };
    if (rangeButtons.length || granularityButtons.length) {
      if (rangeButtons.length !== 2 || granularityButtons.length !== 2 || !note || !doc || new Set(rangeButtons.map((button) => button.dataset.historyRange)).size !== 2 || new Set(granularityButtons.map((button) => button.dataset.historyGranularity)).size !== 2 || rangeButtons.some((button) => !['30', '90'].includes(button.dataset.historyRange)) || granularityButtons.some((button) => !['daily', 'monthly'].includes(button.dataset.historyGranularity))) return;
      if (priceViewButtons.length && (priceViewButtons.length !== 2 || !focusNote || !rangeNote || !rangeLegend || new Set(priceViewButtons.map((button) => button.dataset.historyPriceView)).size !== 2 || priceViewButtons.some((button) => !['focus', 'range'].includes(button.dataset.historyPriceView)))) return;
      if (!render()) return;
      if (displayControls) displayControls.hidden = false;
      for (const button of rangeButtons) button.addEventListener('click', () => { range = Number(button.dataset.historyRange); render(); });
      for (const button of granularityButtons) button.addEventListener('click', () => { granularity = button.dataset.historyGranularity; render(); });
      for (const button of priceViewButtons) button.addEventListener('click', () => { priceView = button.dataset.historyPriceView; render(); });
    }
    const inspectPointer = (event) => {
      if (!positioned.length) return;
      const matrix = svg.getScreenCTM?.();
      if (!matrix) return;
      const cursor = svg.createSVGPoint();
      cursor.x = event.clientX; cursor.y = event.clientY;
      const local = cursor.matrixTransform(matrix.inverse());
      const low = Math.min(...positioned.map((point) => point.x)), high = Math.max(...positioned.map((point) => point.x));
      if (local.x < low - 10 || local.x > high + 10) return;
      const closest = positioned.reduce((best, point) => Math.abs(point.x - local.x) < Math.abs(best.x - local.x) ? point : best);
      if (active !== closest.date) selectDay(closest.date);
    };
    svg.addEventListener('pointermove', (event) => { if (event.pointerType !== 'touch') inspectPointer(event); });
    svg.addEventListener('pointerdown', inspectPointer);
    svg.setAttribute('tabindex', '0');
    svg.addEventListener('keydown', (event) => {
      let index = displayed.findIndex((row) => row.date === active);
      if (event.key === 'ArrowLeft') index -= 1;
      else if (event.key === 'ArrowRight') index += 1;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = displayed.length - 1;
      else return;
      event.preventDefault(); selectDay(displayed[Math.max(0, Math.min(displayed.length - 1, index))].date);
    });
    selectDay(select.value);
    controls.hidden = false;
    root.dataset.historyInteraction = 'ready';
  }
  function boot(scope) {
    if (scope.matches?.('[data-history-ssr]')) attach(scope);
    scope.querySelectorAll?.('[data-history-ssr]').forEach(attach);
  }
  return Object.freeze({ parseRows, displayValue, filterRows, aggregateMonths, buildGeometry, boot });
});
